// =============================================================================
// selectors/practice.ts — the Fix-it gym's numbers, for ui/.
// -----------------------------------------------------------------------------
// A pass-through plus one join. `ui/` may not import `domain/`, so the session
// summary and the sort arrive through here.
// =============================================================================

import {
  isAnnotated, sortPracticeSessions, summarisePracticeSession,
  type PracticeSessionSummary, type PracticeTradeLike,
} from '@domain/practice';
import type { PracticeSession, PracticeTrade } from '@io/sync/practice';

export { isAnnotated, sortPracticeSessions, summarisePracticeSession };
export type { PracticeSessionSummary, PracticeTradeLike };

/**
 * The trades belonging to one session.
 *
 * ⚠ Filtered by `sessionId`, never by date. Two imports covering the same market
 * day are two sessions — matching on the date would merge them, which is the
 * exact thing PRACTICE_PLAN.md says the feature must not do.
 */
export function tradesForSession(
  trades: readonly PracticeTrade[], sessionId: string,
): PracticeTrade[] {
  return trades.filter((t) => t.sessionId === sessionId);
}

/** One row per session, with its summary — what the session list renders. */
export interface PracticeSessionRow {
  session: PracticeSession;
  trades: readonly PracticeTrade[];
  summary: PracticeSessionSummary;
}

export function practiceSessionRows(
  sessions: readonly PracticeSession[],
  trades: readonly PracticeTrade[],
): PracticeSessionRow[] {
  return sortPracticeSessions(sessions).map((session) => {
    const own = tradesForSession(trades, session.id);
    return {
      session,
      trades: own,
      summary: summarisePracticeSession(own.map((t) => t.trade as PracticeTradeLike)),
    };
  });
}
